import type {
    OperationAuditAction,
    OperationHistoryVersion,
    OperationVersionTimelineStatus,
} from '@/api/models';
import {
    formatGeneratedAt,
    isoToDisplayDate,
} from '@/pages/accounting/statements/statementMeta';
import { diffVersions, type VersionDiff } from './operationDiff';

/**
 * The History drawer's presentational config and its derivations.
 *
 * The load-bearing one is {@link toTimeline}: it walks the version list once and
 * pairs each version with its predecessor's diff. The diff itself lives in
 * `operationDiff.ts` and is tested on its own — this only decides *which two
 * versions* get compared, and how the result is said in words.
 */

/**
 * Chip and rail dot per timeline status.
 *
 * **Superseded is muted, not danger**, the rule this folder already applies to a
 * blocked template and an archived account: a version that stopped applying was
 * never wrong, and colouring the whole past of a well-run template as a fault
 * would make the timeline unreadable.
 *
 * **Scheduled is the one that has to stand out**, because it is the state most
 * likely to be misread: it is published, frozen, and applies to nothing yet.
 *
 * The dot is the rail's only colour, carrying the same distinction as the chip
 * beside it — so a reader scanning the rail finds the live version without
 * reading a word.
 */
export const VERSION_STATUS_CHIP: Record<
    OperationVersionTimelineStatus,
    { className: string; dotClassName: string; labelKey: string }
> = {
    ACTIVE: {
        className: 'bg-success-soft text-success-soft-foreground',
        dotClassName: 'bg-success',
        labelKey: 'operations.history.status.active',
    },
    DRAFT: {
        className: 'bg-warning-soft text-warning-soft-foreground',
        dotClassName: 'bg-warning',
        labelKey: 'operations.history.status.draft',
    },
    SCHEDULED: {
        className: 'bg-primary-soft text-primary-soft-foreground',
        dotClassName: 'bg-primary',
        labelKey: 'operations.history.status.scheduled',
    },
    SUPERSEDED: {
        className: 'bg-surface-secondary text-muted',
        dotClassName: 'bg-separator',
        labelKey: 'operations.history.status.superseded',
    },
    BLOCKED: {
        className: 'bg-surface-secondary text-muted',
        dotClassName: 'bg-separator',
        labelKey: 'operations.history.status.blocked',
    },
};

/** i18n key per change-log action. The actor rides in its own column. */
export const AUDIT_ACTION_LABEL: Record<OperationAuditAction, string> = {
    DRAFT_SAVED: 'operations.history.actions.draftSaved',
    PUBLISHED: 'operations.history.actions.published',
    BLOCKED: 'operations.history.actions.blocked',
    UNBLOCKED: 'operations.history.actions.unblocked',
};

/** One card on the timeline: a version, and what it changed. */
export type TimelineEntry = {
    version: OperationHistoryVersion;
    /**
     * The diff against the version numbered one lower. `null` on the **first**
     * version, which has nothing to be compared against — the card then reads as
     * the original recipe rather than as a change to one.
     */
    diff: VersionDiff | null;
};

/**
 * Pair each version with the diff against its predecessor.
 *
 * **Predecessor by version number, not by list position.** The list arrives
 * newest-first and a template's numbers are monotonic but need not be
 * contiguous — a deleted draft leaves a hole, because version numbers are never
 * reused. Walking the list by index would silently compare v5 against v3 and
 * report the two changes as one.
 */
export const toTimeline = (
    versions: readonly OperationHistoryVersion[],
): TimelineEntry[] => {
    const byVersion = new Map(versions.map((v) => [v.version, v]));
    const lowest = Math.min(...versions.map((v) => v.version));

    return versions.map((version) => {
        if (version.version === lowest) {
            return { version, diff: null };
        }
        // The nearest lower number that exists, so a hole in the sequence
        // compares against what actually preceded it.
        let candidate = version.version - 1;
        while (candidate >= lowest && !byVersion.has(candidate)) {
            candidate -= 1;
        }
        const previous = byVersion.get(candidate);
        return {
            version,
            diff: previous ? diffVersions(previous.lines, version.lines) : null,
        };
    });
};

/**
 * How many **lines** one version moved.
 *
 * Lines rather than pairs, because that is the unit an author recognises from
 * the editor: swapping the debit of one pair is one change, and rewriting both
 * of its legs is two. An added or removed pair counts the legs it actually
 * carries — a half-written pair is one line, not two.
 */
export const countLineChanges = (diff: VersionDiff | null): number => {
    if (diff === null) {
        return 0;
    }
    return diff.pairs.reduce((total, pair) => {
        if (pair.status === 'UNCHANGED') {
            return total;
        }
        if (pair.status === 'ADDED') {
            return total + pair.lines.filter((line) => line.after).length;
        }
        if (pair.status === 'REMOVED') {
            return total + pair.lines.filter((line) => line.before).length;
        }
        return (
            total + pair.lines.filter((line) => line.changes.length > 0).length
        );
    }, 0);
};

/** A phrase on a version card's summary line, joined with a dot by the View. */
export type VersionClause = {
    key: string;
    params?: Record<string, string | number>;
};

/**
 * The summary line under a version's number: when it applied, how much it
 * moved, and whether it can still be edited.
 *
 * Clauses rather than a finished sentence, because the joining and the
 * translating belong to the View — this file has no `t`. The order is the order
 * the questions get asked: *when did this apply, what did it change, can I touch
 * it.*
 *
 * **A draft says "not published yet" where a published version says
 * "read-only".** They are two halves of the same fact and the one thing a reader
 * most needs from this line: a draft's date is a plan, a published version's
 * date is a record.
 */
export const versionClauses = (entry: TimelineEntry): VersionClause[] => {
    const { version, diff } = entry;
    const clauses: VersionClause[] = [];

    if (version.status === 'DRAFT' || version.status === 'SCHEDULED') {
        clauses.push({
            key: 'operations.history.clause.effectiveFrom',
            params: { date: isoToDisplayDate(version.effectiveFrom) },
        });
    } else if (version.effectiveTo) {
        clauses.push({
            key: 'operations.history.clause.window',
            params: {
                from: isoToDisplayDate(version.effectiveFrom),
                to: isoToDisplayDate(version.effectiveTo),
            },
        });
    } else {
        clauses.push({
            key: 'operations.history.clause.inForceSince',
            params: { date: isoToDisplayDate(version.effectiveFrom) },
        });
    }

    if (diff === null) {
        clauses.push({ key: 'operations.history.clause.original' });
    } else {
        const changed = countLineChanges(diff);
        clauses.push(
            changed === 0
                ? { key: 'operations.history.clause.noChanges' }
                : {
                      key: 'operations.history.clause.lineChanges',
                      params: { count: changed },
                  },
        );
    }

    clauses.push({
        key:
            version.status === 'DRAFT'
                ? 'operations.history.clause.notPublished'
                : version.status === 'BLOCKED'
                  ? 'operations.history.clause.retired'
                  : 'operations.history.clause.readOnly',
    });

    return clauses;
};

/**
 * Who made the version and when, as the card's right-hand line reads it.
 *
 * A key plus params rather than a finished string, for the seeded case: a
 * version the system wrote has no author, and printing a dash where a name goes
 * says the name is missing rather than that nobody claimed it.
 */
export const authorClause = (
    version: OperationHistoryVersion,
): VersionClause =>
    version.createdByName
        ? {
              key: 'operations.history.author',
              params: {
                  who: version.createdByName,
                  at: formatGeneratedAt(new Date(version.createdAt)),
              },
          }
        : { key: 'operations.history.seeded' };

/**
 * The draft a reader could open from here, if there is one.
 *
 * At most one exists — the server keeps a single open draft per template — and
 * it is always the highest number, since numbers are never reused.
 */
export const draftVersionOf = (
    versions: readonly OperationHistoryVersion[],
): OperationHistoryVersion | null =>
    versions.find((version) => version.status === 'DRAFT') ?? null;
